import { convertPrice } from '@/utils/common';
import { buildTabKpisFromApi } from './reportAggregates';

const formatMetric = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  return convertPrice(value);
};

const formatCount = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  return String(value);
};

export const buildTabKpis = ({
  tabKey,
  summary = null,
  totals = null,
  totalRow = null,
  total = null,
  pagination = null,
  rows = [],
  storeCount = 0,
  overview = {},
}) => {
  const metrics = buildTabKpisFromApi(tabKey, {
    summary,
    totals,
    totalRow,
    total,
    totalItems: Number(pagination?.totalItems) || 0,
    rows,
    storeCount,
  });

  switch (tabKey) {
    case 'overview':
      return [
        { label: 'Doanh thu kỳ', value: convertPrice(overview.revenue || 0), tone: 'primary' },
        {
          label: 'Lãi gộp kỳ',
          value: convertPrice(overview.grossProfit || 0),
          tone: 'success',
          hint: `${((Number(overview.margin || 0) * 100) || 0).toFixed(1)}% margin`,
        },
        { label: 'Giá trị tồn kho', value: convertPrice(overview.inventoryValue || 0), tone: 'warning' },
        { label: 'SKU đang theo dõi', value: String(overview.skuCount || 0), tone: 'default' },
      ];
    case 'by-product':
      return [
        { label: 'Tổng SL bán', value: formatMetric(metrics.soLuong), tone: 'default' },
        { label: 'Tổng doanh số', value: formatMetric(metrics.doanhThu), tone: 'primary' },
        { label: 'Tổng lãi', value: formatMetric(metrics.laiGop), tone: 'success' },
        { label: 'Số SP', value: formatCount(metrics.count), tone: 'default' },
      ];
    case 'by-bill':
      return [
        { label: 'Tổng hoá đơn', value: formatCount(metrics.count), tone: 'default' },
        { label: 'Tổng SP bán', value: formatMetric(metrics.soLuong), tone: 'default' },
        { label: 'Doanh thu', value: formatMetric(metrics.doanhThu), tone: 'primary' },
        { label: 'Lãi gộp', value: formatMetric(metrics.laiGop), tone: 'success' },
      ];
    case 'by-imei':
      return [
        { label: 'Số IMEI', value: formatCount(metrics.count), tone: 'default' },
        { label: 'Tổng giá bán', value: formatMetric(metrics.giaBan), tone: 'primary' },
        { label: 'Tổng giá net', value: formatMetric(metrics.giaNet), tone: 'warning' },
        { label: 'Lãi gộp', value: formatMetric(metrics.laiGop), tone: 'success' },
      ];
    case 'purchase-by-imei':
      return [
        { label: 'Số dòng IMEI', value: formatCount(metrics.lineCount), tone: 'default' },
        { label: 'Tổng SL nhập', value: formatMetric(metrics.soLuong), tone: 'default' },
        { label: 'Giá trị nhập', value: formatMetric(metrics.giaTriNhap), tone: 'warning' },
        { label: 'Số NCC', value: formatCount(metrics.soNcc), tone: 'primary' },
      ];
    case 'by-bill-type': {
      const revenue = metrics.doanhThu;
      const profit = metrics.laiGop;
      return [
        { label: 'Tổng đơn', value: formatMetric(metrics.slDon), tone: 'default' },
        { label: 'Doanh thu thuần', value: formatMetric(revenue), tone: 'primary' },
        {
          label: 'Lãi gộp',
          value: formatMetric(profit),
          tone: 'success',
          hint:
            revenue != null && profit != null
              ? `${((profit / revenue) * 100 || 0).toFixed(1)}% margin`
              : '',
        },
        { label: 'Trả hàng', value: formatMetric(metrics.traHang), tone: 'danger' },
      ];
    }
    case 'by-store-category':
      return [
        { label: 'Số cửa hàng', value: formatCount(metrics.storeCount), tone: 'default' },
        { label: 'Tổng đơn', value: formatMetric(metrics.slDon), tone: 'default' },
        { label: 'Doanh thu', value: formatMetric(metrics.doanhThu), tone: 'primary' },
        { label: 'Lãi gộp', value: formatMetric(metrics.laiGop), tone: 'success' },
      ];
    case 'purchases':
      return [
        { label: 'Tổng SL nhập', value: formatMetric(metrics.soLuong), tone: 'default' },
        {
          label: 'Thành tiền sau VAT',
          value: formatMetric(metrics.thanhTienSauVat),
          tone: 'warning',
        },
        { label: 'Số dòng', value: formatCount(metrics.lineCount), tone: 'default' },
      ];
    case 'by-spc':
      return [
        { label: 'Tổng SL bán', value: formatMetric(metrics.soLuong), tone: 'default' },
        { label: 'Tổng doanh số', value: formatMetric(metrics.tongDoanhSo), tone: 'primary' },
        { label: 'Tổng lãi', value: formatMetric(metrics.tongLai), tone: 'success' },
        { label: 'Số SPC', value: formatCount(metrics.count), tone: 'default' },
      ];
    case 'by-customer': {
      const revenue = metrics.doanhThu;
      const profit = metrics.laiGop;
      return [
        { label: 'Số khách', value: formatCount(metrics.count), tone: 'default' },
        { label: 'Tổng SL bán', value: formatMetric(metrics.soLuong), tone: 'default' },
        { label: 'Doanh thu', value: formatMetric(revenue), tone: 'primary' },
        {
          label: 'Lãi gộp',
          value: formatMetric(profit),
          tone: 'success',
          hint:
            revenue != null && profit != null
              ? `${((profit / revenue) * 100 || 0).toFixed(1)}% margin`
              : '',
        },
      ];
    }
    case 'by-customer-product':
      return [
        { label: 'Số dòng', value: formatCount(metrics.count), tone: 'default' },
        { label: 'Tổng SL', value: formatMetric(metrics.soLuong), tone: 'default' },
        { label: 'Doanh thu', value: formatMetric(metrics.doanhThu), tone: 'primary' },
        { label: 'Lãi gộp', value: formatMetric(metrics.laiGop), tone: 'success' },
      ];
    case 'inventory-value-by-store': {
      const totalValue = metrics.tongGiaTriTonKho;
      const skuTotal = metrics.slHangHoa;
      const storeCount = metrics.count;
      const avgPerStore =
        totalValue != null && storeCount > 0 ? totalValue / storeCount : null;
      return [
        { label: 'Số kho', value: formatCount(storeCount), tone: 'default' },
        { label: 'Tổng SL hàng hoá', value: formatMetric(skuTotal), tone: 'default' },
        { label: 'Tổng giá trị tồn kho', value: formatMetric(totalValue), tone: 'warning' },
        { label: 'Giá trị TB / kho', value: formatMetric(avgPerStore), tone: 'primary' },
      ];
    }
    default:
      return [];
  }
};
